/**
 * Life4Earth V2 — React hook to fetch materialized rhythm task
 * instances for a given community role slot and calendar year.
 *
 * DEV-1224 (2026-06-03). Powers the year-at-a-glance ribbon's per-dot
 * status glyph by exposing the slot's actual instance history (not the
 * synthesized cadence positions the ribbon used before). When DEV-1225
 * lands, the same data drives the historical-tray view — the DTO
 * already carries the audit metadata that ticket needs (completedAt,
 * completedByDisplayName, skipReason).
 *
 * Idle when slotGuid is nullish. The empty-array return is the natural
 * "no instances yet" state — caller should treat that the same as the
 * legacy "no per-dot data" case (render plain dots).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchSlotRhythmInstances } from "./roleRhythms";
import type { SlotRhythmInstanceResponse } from "./roleRhythms";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

export interface UseApiSlotRhythmInstancesResult {
  instances: SlotRhythmInstanceResponse[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function cacheKeyFor(slotGuid: string, year: number): string {
  return `slotRhythmInstances:${slotGuid.toLowerCase()}:${year}`;
}

export function useApiSlotRhythmInstances(
  slotGuid: string | null | undefined,
  year?: number,
): UseApiSlotRhythmInstancesResult {
  const mountedRef = useRef(true);
  const targetYear = year ?? new Date().getFullYear();

  const [data, setData] = useState<UseApiSlotRhythmInstancesResult>({
    instances: [],
    loading: !!slotGuid,
    error: null,
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;
    if (!slotGuid) {
      setData({ instances: [], loading: false, error: null, refetch });
      return;
    }
    try {
      const instances = await fetchForKey<SlotRhythmInstanceResponse[]>(
        cacheKeyFor(slotGuid, targetYear),
        () => fetchSlotRhythmInstances(slotGuid, targetYear),
        { force: true },
      );
      if (!mountedRef.current) return;
      setData({
        instances: instances ?? [],
        loading: false,
        error: null,
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        instances: [],
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        refetch,
      }));
    }
  }, [slotGuid, targetYear]);

  useEffect(() => {
    mountedRef.current = true;
    void refetch();

    const onCacheUpdate = (e: Event) => {
      if (!slotGuid) return;
      const detail = (e as CustomEvent<{ key: string }>).detail;
      if (detail?.key === cacheKeyFor(slotGuid, targetYear)) void refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
    };
  }, [refetch, slotGuid, targetYear]);

  return data;
}
